<template>
  <div class="columns is-multiline">
    <div class="column is-3" v-if="afficherCode">
      <b-field :label="$t('addEditBailleur.informationsGenerales.code')">
        <b-input disabled v-model="bailleur.code"></b-input>
      </b-field>
    </div>
    <div class="column is-3" v-if="!isParticulier">
      <input-tier-nom-commercial
          :label="$t('addEditBailleur.informationsGenerales.raisonSociale')"
          v-model="bailleur.nomCommercial"
          :tierId="bailleur.id"
          :query="bailleurQuery"
          path="c.baux.bailleur">
      </input-tier-nom-commercial>
    </div>
    <div class="column is-3" v-if="isParticulier">
      <parametre-autocomplete
        :label="$t('addEditBailleur.informationsGenerales.civilite')"
        v-model="bailleur.civiliteId"
        :target="constantes.PARAMETRES_KEYS.civilite"
        expanded
      />
    </div>
    <div class="column is-3" v-if="isParticulier">
      <input-tier-nom-commercial
          :label="$t('addEditBailleur.informationsGenerales.nom')"
          v-model="bailleur.nom"
          :tierId="bailleur.id"
          :query="bailleurQuery"
          path="c.baux.bailleur">
      </input-tier-nom-commercial>
    </div>
    <div class="column is-3" v-if="isParticulier">
      <b-field :label="$t('addEditBailleur.informationsGenerales.prenom')">
        <b-input v-model="bailleur.prenom"></b-input>
      </b-field>
    </div>
    <div class="column is-3" v-if="isParticulier">
      <b-field :label="$t('addEditBailleur.informationsGenerales.dateNaissance')">
        <b-datepicker show-week-number
                nearby-selectable-month-days
                v-model="bailleur.dateNaissance"
                icon="calendar-today"
                :editable="true">
        </b-datepicker>
      </b-field>
    </div>
    <div class="column is-3" v-if="isParticulier">
      <b-field :label="$t('addEditBailleur.informationsGenerales.nomJeuneFille')">
        <b-input v-model="bailleur.nomJeuneFille"></b-input>
      </b-field>
    </div>
    <div class="column is-3">
      <input-email :label="$t('addEditBailleur.informationsGenerales.email')"
                   v-model="bailleur.email"/>
    </div>
    <div class="column is-3">
      <b-field :label="$t('addEditBailleur.informationsGenerales.telephoneFixe')">
        <b-input v-model="bailleur.telephone"></b-input>
      </b-field>
    </div>
    <div class="column is-3">
      <b-field :label="$t('addEditBailleur.informationsGenerales.telephonePortable')">
        <b-input v-model="bailleur.portable"></b-input>
      </b-field>
    </div>
    <div class="column is-3" v-if="afficherRole">
      <libelle-tag-input
        v-model="bailleur.sousTypes"
        :label="$t('addEditBailleur.informationsGenerales.role')"
        :items="sousTypePossible"
        @input="checkSousType"
      ></libelle-tag-input>
    </div>
    <div class="column is-3">
      <b-field :label="$t('addEditBailleur.informationsGenerales.applicationTva')">
        <b-select v-model="bailleur.applicationTva" expanded>
          <option
            v-for="item in constantes.LISTE_APPLICATION_TVA"
            :value="item.code"
            :key="item.code">
            {{ $t(item.libelle) }}
          </option>
        </b-select>
      </b-field>
    </div>
    <div class="column is-3" v-if="!isParticulier">
      <b-field
        :label="$t('addEditBailleur.informationsGenerales.siret')">
        <b-input v-model="bailleur.siret"></b-input>
      </b-field>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import {
  TIERS_SOUS_TYPE_BAILLEUR_PRIVE,
  LISTE_SOUS_TYPE_BAILLEUR,
  TIERS_TYPE_FOURNISSEUR,
  LISTE_APPLICATION_TVA,
  LISTE_SOUS_TYPE,
  LISTE_SOUS_TYPE_WITHOUT_BAILLEUR,
} from '@/configuration/tiers/tiersConf';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import inputEmail from '@/components/general/inputEmail';
import traductionLibelleEtat from '@/services/devisCommandes/traductionLibelleEtat';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';
import inputTierNomCommercial from '@/components/general/input/tierNomCommercialInput/tierNomCommercialInput';
import libelleTagInput from '@/components/general/tagInput/libelleTagInput';
import parametreAutocomplete from '@/components/general/autocomplete/parametreAutocomplete';

const Societe = createNamespacedHelpers('societe');

export default {
  props: {
    bailleur: {
      type: Object,
      required: true,
    },
    afficherCode: {
      type: Boolean,
      required: false,
      default: true,
    },
    afficherRole: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      constantes: {
        LISTE_APPLICATION_TVA,
        PARAMETRES_KEYS,
      },
    };
  },
  components: {
    inputEmail,
    inputTierNomCommercial,
    libelleTagInput,
    parametreAutocomplete,
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    isParticulier() {
      return this.bailleur.sousTypes.find((sousType) => {
        return sousType.code === TIERS_SOUS_TYPE_BAILLEUR_PRIVE;
      });
    },
    getQueryNom() {
      const tab = [];
      if (this.bailleur.nomCommercial) tab.push(this.bailleur.nomCommercial);
      if (this.bailleur.nom) tab.push(this.bailleur.nom);
      return tab;
    },
    sousTypePossible() {
      const codesBailleur = LISTE_SOUS_TYPE_BAILLEUR.map(st => st.code);
      return this.bailleur.sousTypes.some(st => codesBailleur.includes(st.code))
        ? traductionLibelleEtat(this.$i18n, LISTE_SOUS_TYPE_WITHOUT_BAILLEUR)
        : traductionLibelleEtat(this.$i18n, LISTE_SOUS_TYPE);
    },
    bailleurQuery() {
      return {
        nomsCommercialOuNomsJuridique: this.getQueryNom,
        societeId: this.societeActiveId,
        type: TIERS_TYPE_FOURNISSEUR,
        sousType: this.bailleur.sousTypes
          ? this.bailleur.sousTypes.map(st => st.code)
          : [this.$route.params.sousType],
      };
    },
  },
  methods: {
    checkSousType() {
      if (this.isParticulier && this.bailleur.nomCommercial) {
        this.bailleur.nom = this.bailleur.nomCommercial;
        this.bailleur.nomCommercial = null;
      }

      if (!this.isParticulier && this.bailleur.nom) {
        this.bailleur.nomCommercial = nomPrenomGetter(this.bailleur);
        this.bailleur.nom = null;
        this.bailleur.prenom = null;
      }
    },
  },
};
</script>
